import {
  FINANCE_OFFICE_RECEIPT_STATUS,
  FINANCE_OFFICE_SCHEMA_VERSION,
  validateFinanceOfficeOperation,
} from './contracts';

const INSPECTION_STATUS = Object.freeze({
  NOT_COMMITTED: 'not_committed',
  COMMITTED: 'committed',
  INDETERMINATE: 'indeterminate',
});

export class FinanceOfficeOperationError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'FinanceOfficeOperationError';
    this.code = code;
  }
}

function operationError(code, message) {
  return new FinanceOfficeOperationError(code, message);
}

function targetType(app) {
  if (app === 'powerpoint') return 'slide';
  if (app === 'excel') return 'worksheet_range';
  if (app === 'word') return 'content_control';
  throw operationError(
    'invalid_finance_office_operation',
    'Finance Office app is not supported'
  );
}

function stableTargetId(request) {
  if (request.app === 'powerpoint') return request.target.slideId;
  if (request.app === 'excel') {
    return `${request.target.worksheetId}!${request.target.anchorCell}`;
  }
  if (request.app === 'word') return request.target.contentControlTag;
  throw operationError(
    'invalid_finance_office_operation',
    'Finance Office app is not supported'
  );
}

function validateInspection(value) {
  if (
    !value ||
    typeof value !== 'object' ||
    Array.isArray(value) ||
    !Object.values(INSPECTION_STATUS).includes(value.status)
  ) {
    throw operationError(
      'finance_office_verification_failed',
      'Finance Office operation verification was ambiguous'
    );
  }
  return {
    status: value.status,
    mutationCount: Number.isSafeInteger(value.mutationCount) &&
      value.mutationCount >= 0 &&
      value.mutationCount <= 10_000
      ? value.mutationCount
      : 0,
  };
}

function validateCommitResult(value) {
  if (
    !value ||
    typeof value !== 'object' ||
    Array.isArray(value) ||
    value.committed !== true ||
    !Number.isSafeInteger(value.mutationCount) ||
    value.mutationCount < 1 ||
    value.mutationCount > 10_000
  ) {
    throw operationError(
      'finance_office_verification_failed',
      'Finance Office operation did not return a verified commit'
    );
  }
  return { committed: true, mutationCount: value.mutationCount };
}

function receiptFor(request, {
  status,
  mutationCount = 0,
  startedAt,
  completedAt = null,
}) {
  return Object.freeze({
    schemaVersion: FINANCE_OFFICE_SCHEMA_VERSION,
    operationId: request.operationId,
    operationBinding: request.operationBinding,
    stepId: request.stepId,
    documentId: request.documentId,
    app: request.app,
    command: request.command,
    targetType: targetType(request.app),
    targetId: stableTargetId(request),
    status,
    mutationCount,
    startedAt,
    completedAt,
  });
}

function isImmutableCommitted(receipt) {
  return [
    FINANCE_OFFICE_RECEIPT_STATUS.COMMITTED,
    FINANCE_OFFICE_RECEIPT_STATUS.COMMITTED_AFTER_CANCEL,
  ].includes(receipt?.status);
}

export class FinanceOfficeOperationRegistry {
  constructor({
    documentPrivacy,
    writers,
    now = () => new Date(),
  }) {
    if (
      !documentPrivacy ||
      typeof documentPrivacy.getState !== 'function' ||
      typeof documentPrivacy.assertCurrentFileIdentity !== 'function' ||
      typeof documentPrivacy.markLicensedData !== 'function' ||
      typeof documentPrivacy.getReceipt !== 'function' ||
      typeof documentPrivacy.putReceipt !== 'function' ||
      !(writers instanceof Map)
    ) {
      throw operationError(
        'finance_office_unavailable',
        'Finance Office operation registry is not configured'
      );
    }
    this.documentPrivacy = documentPrivacy;
    this.writers = writers;
    this.now = now;
    this.inFlight = new Map();
    this.memoryReceipts = new Map();
  }

  _writer(request) {
    const writer = this.writers.get(request.command);
    if (
      !writer ||
      typeof writer.prepare !== 'function' ||
      typeof writer.inspect !== 'function' ||
      typeof writer.commit !== 'function'
    ) {
      throw operationError(
        'finance_office_command_unsupported',
        'Finance Office command is not implemented'
      );
    }
    return writer;
  }

  async _assertDocument(request) {
    const state = await this.documentPrivacy.getState();
    if (state.documentId !== request.documentId) {
      throw operationError(
        'finance_document_identity_mismatch',
        'Finance Office operation targets a different document'
      );
    }
    return state;
  }

  async _assertCurrentIdentity(request) {
    const state = await this.documentPrivacy.assertCurrentFileIdentity(
      request.documentId
    );
    if (state.documentId !== request.documentId) {
      throw operationError(
        'finance_document_identity_mismatch',
        'Finance Office operation targets a different document'
      );
    }
    return state;
  }

  _writerOptions(request, signal) {
    return {
      signal,
      assertCurrentIdentity: () => this._assertCurrentIdentity(request),
    };
  }

  async _currentReceipt(request) {
    const persisted = await this.documentPrivacy.getReceipt(request.operationId);
    const receipt = persisted || this.memoryReceipts.get(request.operationId) || null;
    if (
      receipt &&
      (
        receipt.documentId !== request.documentId ||
        receipt.stepId !== request.stepId ||
        receipt.operationBinding !== request.operationBinding ||
        receipt.app !== request.app ||
        receipt.command !== request.command ||
        receipt.targetId !== stableTargetId(request)
      )
    ) {
      throw operationError(
        'finance_office_operation_conflict',
        'Finance Office operation does not match its prior receipt'
      );
    }
    return receipt;
  }

  async _store(receipt) {
    const stored = await this.documentPrivacy.putReceipt(receipt);
    this.memoryReceipts.set(receipt.operationId, stored);
    return Object.freeze({ ...stored });
  }

  _serialize(request, operation) {
    const operationId = request.operationId;
    const fingerprint = JSON.stringify({
      operationBinding: request.operationBinding,
      stepId: request.stepId,
      documentId: request.documentId,
      app: request.app,
      command: request.command,
      target: request.target,
      payload: request.payload,
    });
    const existing = this.inFlight.get(operationId);
    if (existing) {
      if (existing.fingerprint !== fingerprint) {
        throw operationError(
          'finance_office_operation_conflict',
          'Finance Office operation does not match the in-flight request'
        );
      }
      return existing.promise;
    }
    const run = Promise.resolve().then(operation);
    const entry = { fingerprint, promise: null };
    this.inFlight.set(operationId, entry);
    entry.promise = run.finally(() => {
      if (this.inFlight.get(operationId) === entry) {
        this.inFlight.delete(operationId);
      }
    });
    return entry.promise;
  }

  async prepare(rawRequest, { signal } = {}) {
    const request = validateFinanceOfficeOperation(rawRequest);
    return this._serialize(request, async () => {
      await this._assertDocument(request);
      const current = await this._currentReceipt(request);
      if (current) return Object.freeze({ ...current });
      if (signal?.aborted) {
        const now = this.now().toISOString();
        return this._store(receiptFor(request, {
          status: FINANCE_OFFICE_RECEIPT_STATUS.NOT_COMMITTED,
          startedAt: now,
          completedAt: now,
        }));
      }
      await this._assertCurrentIdentity(request);
      await this._writer(request).prepare(
        request,
        this._writerOptions(request, signal)
      );
      return this._store(receiptFor(request, {
        status: FINANCE_OFFICE_RECEIPT_STATUS.NOT_COMMITTED,
        startedAt: this.now().toISOString(),
      }));
    });
  }

  async commit(rawRequest, { signal } = {}) {
    const request = validateFinanceOfficeOperation(rawRequest);
    return this._serialize(request, async () => {
      await this._assertDocument(request);
      const writer = this._writer(request);
      let current = await this._currentReceipt(request);
      if (isImmutableCommitted(current)) {
        return Object.freeze({ ...current });
      }
      if (current?.status === FINANCE_OFFICE_RECEIPT_STATUS.INDETERMINATE) {
        return this._recoverLocked(
          request,
          writer,
          current,
          { signal }
        );
      }
      if (!current) {
        await this._assertCurrentIdentity(request);
        await writer.prepare(
          request,
          this._writerOptions(request, signal)
        );
        current = await this._store(receiptFor(request, {
          status: FINANCE_OFFICE_RECEIPT_STATUS.NOT_COMMITTED,
          startedAt: this.now().toISOString(),
        }));
      }
      if (signal?.aborted) {
        const receipt = receiptFor(request, {
          status: FINANCE_OFFICE_RECEIPT_STATUS.NOT_COMMITTED,
          mutationCount: 0,
          startedAt: current.startedAt,
          completedAt: this.now().toISOString(),
        });
        return this._store(receipt);
      }

      await this._assertCurrentIdentity(request);
      const before = validateInspection(
        await writer.inspect(
          request,
          this._writerOptions(request, signal)
        )
      );
      if (before.status === INSPECTION_STATUS.COMMITTED) {
        await this.documentPrivacy.markLicensedData(request.documentId);
        return this._store(receiptFor(request, {
          status: signal?.aborted
            ? FINANCE_OFFICE_RECEIPT_STATUS.COMMITTED_AFTER_CANCEL
            : FINANCE_OFFICE_RECEIPT_STATUS.COMMITTED,
          mutationCount: before.mutationCount,
          startedAt: current.startedAt,
          completedAt: this.now().toISOString(),
        }));
      }
      if (before.status === INSPECTION_STATUS.INDETERMINATE) {
        return this._store(receiptFor(request, {
          status: FINANCE_OFFICE_RECEIPT_STATUS.INDETERMINATE,
          mutationCount: before.mutationCount,
          startedAt: current.startedAt,
          completedAt: this.now().toISOString(),
        }));
      }

      await this.documentPrivacy.markLicensedData(request.documentId);
      await this._assertCurrentIdentity(request);
      try {
        const result = validateCommitResult(
          await writer.commit(
            request,
            this._writerOptions(request, signal)
          )
        );
        return this._store(receiptFor(request, {
          status: signal?.aborted
            ? FINANCE_OFFICE_RECEIPT_STATUS.COMMITTED_AFTER_CANCEL
            : FINANCE_OFFICE_RECEIPT_STATUS.COMMITTED,
          mutationCount: result.mutationCount,
          startedAt: current.startedAt,
          completedAt: this.now().toISOString(),
        }));
      } catch {
        return this._recoverLocked(
          request,
          writer,
          current,
          { signal }
        );
      }
    });
  }

  async _recoverLocked(
    request,
    writer,
    current,
    { signal } = {}
  ) {
    await this._assertCurrentIdentity(request);
    let inspection;
    try {
      inspection = validateInspection(await writer.inspect(
        request,
        this._writerOptions(request, signal)
      ));
    } catch {
      inspection = { status: INSPECTION_STATUS.INDETERMINATE, mutationCount: 0 };
    }
    if (inspection.status === INSPECTION_STATUS.COMMITTED) {
      await this.documentPrivacy.markLicensedData(request.documentId);
    }
    const status = inspection.status === INSPECTION_STATUS.COMMITTED
      ? (signal?.aborted
        ? FINANCE_OFFICE_RECEIPT_STATUS.COMMITTED_AFTER_CANCEL
        : FINANCE_OFFICE_RECEIPT_STATUS.COMMITTED)
      : inspection.status === INSPECTION_STATUS.NOT_COMMITTED
        ? FINANCE_OFFICE_RECEIPT_STATUS.NOT_COMMITTED
        : FINANCE_OFFICE_RECEIPT_STATUS.INDETERMINATE;
    return this._store(receiptFor(request, {
      status,
      mutationCount: inspection.mutationCount,
      startedAt: current?.startedAt || this.now().toISOString(),
      completedAt: this.now().toISOString(),
    }));
  }

  async recover(rawRequest, { signal } = {}) {
    const request = validateFinanceOfficeOperation(rawRequest);
    return this._serialize(request, async () => {
      await this._assertDocument(request);
      const current = await this._currentReceipt(request);
      if (isImmutableCommitted(current)) {
        return Object.freeze({ ...current });
      }
      return this._recoverLocked(
        request,
        this._writer(request),
        current,
        { signal }
      );
    });
  }
}

export { INSPECTION_STATUS };
export default FinanceOfficeOperationRegistry;
